"use client";

import { AttendanceData, recordAttendance } from "@punchup/data/legacy";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import {
  FeedEvents,
  FollowComedianEvents,
  trackEvent,
} from "@punchup/analytics";
import React, { useCallback, useState } from "react";

import { Check } from "lucide-react";
import { CheckoutModalOrDrawer } from "@punchup/ui-common/components/checkout";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { Loader } from "@/components/common/icons";
import { SpecialRow } from "@/data/specials";
import { cn } from "../common/utils";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { logException } from "@/analytics/sentry";
import { useFollowComedian } from "@/hooks/follows/useFollowComedian";
import { useRouter } from "next/navigation";

// Follow.tsx is the generic follow button used across the feed. New callers
// must add their location here.
const clickEventByLocation: Partial<
  Record<FOLLOW_LOCATIONS, FollowComedianEvents>
> = {
  [FOLLOW_LOCATIONS.FEED_VIDEO_CARD]: FollowComedianEvents.FEED_VIDEO_CARD,
  [FOLLOW_LOCATIONS.FEED_SUGGESTED_COMEDIAN_CARD]:
    FollowComedianEvents.FEED_SUGGESTED_COMEDIAN_CARD,
  [FOLLOW_LOCATIONS.FEED_FEATURED_COMEDIAN_CARD]:
    FollowComedianEvents.FEED_FEATURED_COMEDIAN_CARD,
};

export default function Follow({
  redirectTo = "",
  buttonText = "Join the Guest List",
  isLoggedOutState = false,
  followedLocation,
  comedianToFollow,
  customCSSContainerClass,
  customCSSButtonClass,
  special,
  isSubscribed,
  additionalEventToTrack,
  callback,
  attendanceData,
  ctaSource,
  pageSection,
}: {
  followedLocation: FOLLOW_LOCATIONS;
  comedianToFollow: Comedian;
  isLoggedOutState?: boolean;
  redirectTo?: string | false;
  buttonText?: string;
  customCSSContainerClass?: string;
  customCSSButtonClass?: string;
  special?: SpecialRow;
  isSubscribed?: boolean;
  additionalEventToTrack?: {
    event: string;
    eventProperties: Record<string, any>;
  };
  callback?: () => void;
  attendanceData?: AttendanceData;
  ctaSource?: string;
  pageSection?: string;
}) {
  // Supabase client still needed for attendance recording
  const supabase = createClientComponentClient<Database>({});
  const router = useRouter();
  const [success, setSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { followComedianAsync, isFollowing } = useFollowComedian();

  const handleFollowSubmit = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (isSubscribed) return;

      const clickEvent = clickEventByLocation[followedLocation];
      if (clickEvent) {
        trackEvent(clickEvent, {
          followLocation: followedLocation,
          comedianId: comedianToFollow.id,
          comedianName: comedianToFollow.display_name,
          ...(ctaSource ? { cta_source: ctaSource } : {}),
          ...(pageSection ? { page_section: pageSection } : {}),
        });
      }

      try {
        if (isLoggedOutState) {
          setIsOpen(true);
          if (additionalEventToTrack?.event) {
            trackEvent(additionalEventToTrack.event, {
              ...additionalEventToTrack.eventProperties,
            });
          }
        } else {
          await followComedianAsync({
            comedian: comedianToFollow,
            followLocation: followedLocation,
            ctaSource,
            pageSection,
          });

          // Record attendance if data is available
          if (attendanceData) {
            const { data: userData } = await supabase.auth.getUser();
            if (userData?.user) {
              try {
                await recordAttendance(
                  supabase,
                  userData.user.id,
                  attendanceData.showId,
                  attendanceData.comedianId,
                  attendanceData.venueId
                );
                console.log(
                  `Attendance recorded for user at show ${attendanceData.showId}`
                );
              } catch (error) {
                logException(
                  new Error("Failed to record attendance: " + error)
                );
              }
            }
          }
          setSuccess(true);
          callback && callback();

          if (redirectTo) {
            router.push(redirectTo);
            // Refreshes the current route and fetches new data from the server without
            // losing client-side browser or React state. This should let the component
            // continue to the redirectTo page with the new state, without reloading the page.
            router.refresh();
          }
        }
      } catch (error) {
        logException(error);
        trackEvent("Follow Error", {
          comedianId: comedianToFollow.id,
          comedianName: comedianToFollow.display_name,
          location: followedLocation,
          error: (error as Error).message || "Unknown error",
        });
      }
    },
    [
      isLoggedOutState,
      comedianToFollow,
      followedLocation,
      followComedianAsync,
      supabase,
      redirectTo,
      router,
      isSubscribed,
      attendanceData,
      callback,
      additionalEventToTrack,
      ctaSource,
      pageSection,
    ]
  );

  const buttonClassName = cn(
    customCSSButtonClass || "btn items-center shadow-sm w-full",
    {
      "bg-neutral-900 text-white": success || isSubscribed,
    }
  );

  return (
    <div className={customCSSContainerClass || "w-full max-w-md my-5"}>
      <div className="flex items-center">
        <button
          onClick={handleFollowSubmit}
          type="submit"
          disabled={success || isSubscribed || isFollowing}
          className={buttonClassName}
        >
          {isFollowing ? (
            <Loader />
          ) : isSubscribed || success ? (
            <div className="flex items-center justify-center w-full gap-2">
              <Check
                className="text-white"
                width={17}
                height={17}
                strokeWidth={4}
              />
              <span>Subscribed</span>
            </div>
          ) : (
            buttonText
          )}
        </button>
      </div>
      {/* Only render CheckoutModalOrDrawer if the user is logged out */}
      {isLoggedOutState && (
        <CheckoutModalOrDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          comedianToFollow={comedianToFollow}
          redirectTo={redirectTo || ""}
          special={special}
          followLocation={followedLocation}
          onOpen={(params) => {
            trackEvent(FeedEvents.OPENED_SUBSCRIPTION_MODAL, params);
          }}
        />
      )}
    </div>
  );
}
